import { useSchoolContext } from '@/App/hooks'
import { useCurrentSchoolId } from '@/hooks'
import { isTokenDying } from '@/sessions'
import { REFLECTION_KEY } from '@/store/const'
import { getParseLocalValue } from '@/store/utils'
import { userAgent } from '@seiue/ua'
import { Toast } from '@seiue/ui'
import { isNaN, useOnMount } from '@seiue/util'
import { Spin } from 'antd'
import { authApi, User, UserDomainEnum } from 'packages/sdks-next/growp'
import { getUrlParams } from 'packages/utils'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'

import { MainContent } from './MainContent'

/**
 * 从 pathname 的 `/login/:segment` 中解析学校 ID。
 * 处理 URL 编码，并取 segment 前导数字部分。
 */
const parseSchoolIdFromAdminLoginPath = (
  pathname: string
): { raw: string | undefined; id: string | undefined } => {
  const match = pathname.match(/\/login\/(.*)/)
  const raw = match?.[1]
  if (!raw) return { raw: undefined, id: undefined }

  try {
    const decoded = decodeURIComponent(raw)
    const digitMatch = decoded.match(/^(\d+)/)
    return { raw, id: digitMatch?.[1] }
  } catch {
    const digitMatch = raw.match(/^(\d+)/)
    return { raw, id: digitMatch?.[1] }
  }
}

/**
 * 教师界面登录
 *
 * @param param - 参数
 * @param param.isPreview - 是否是预览
 * @returns login page
 */
export const AdminLogin: React.FC<{
  isPreview?: boolean
}> = ({ isPreview }) => {
  const [loading, setLoading] = useState<boolean>(true)

  const data = useSchoolContext()
  const schoolId = useCurrentSchoolId()

  // 直接从 local storage 获取 reflection，避免使用 useCurrentReflection hook 导致数据不一致
  const reflection = getParseLocalValue(REFLECTION_KEY) as User | undefined

  const { raw: urlSchoolIdRaw, id: urlSchoolId } =
    parseSchoolIdFromAdminLoginPath(location.pathname)

  useOnMount(() => {
    if (!isPreview) {
      if (!urlSchoolId || isNaN(Number(urlSchoolId))) {
        if (urlSchoolIdRaw?.includes('/')) {
          Toast.warning('路由错误')
        } else {
          Toast.warning('未找到学校信息，请确认登录链接是否正确')
        }

        location.href = '/404'
      }
    }
  })

  const isOidc = !!getUrlParams('client_id')
  const openLogin = {
    clientId: getUrlParams('client_id'),
    scope: getUrlParams('scope'),
    state: getUrlParams('state'),
    redirectUri: getUrlParams('redirect_uri'),
    responseType: getUrlParams('response_type'),
    nonce: getUrlParams('nonce'),
  }

  useOnMount(() => {
    if (reflection && !isTokenDying(reflection.expiredAt) && isOidc) {
      // 如果是来自 oidc 请求并且当前用户已登陆，静默触发 oidc 流程
      const postData = {
        openLogin,
        // 暂时只有一个学校使用，后期可以扩展
        // schoolId: Number(urlSchoolId),
        // NEED TO FIX HACK
        schoolId: 3,
        account: '',
        password: '',
      }

      authApi
        .login(UserDomainEnum.Formal, postData)
        .then((response) => {
          if (response.data.redirectUri) {
            location.href = response.data.redirectUri
            return
          }
        })
        .catch((e) => {
          setLoading(false)
          Toast.error(e.response?.data?.message || '登录失败')
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false)
          }, 1000)
        })
    } else {
      setLoading(false)
    }
  })

  if ((!isPreview && isNaN(Number(urlSchoolId))) || loading)
    return <StyledSpin />

  return (
    <Wrapper>
      {userAgent.isMobile ? null : (
        <ImageWrapper
          style={{
            backgroundImage: `url(${data?.bgImage || '/login-bg.png'})`,
          }}
        >
          <Mask>
            {schoolId === 3 ? <OuterImg src={'/bdfz-icon.png'} /> : null}
          </Mask>
        </ImageWrapper>
      )}

      <ContentWrapper>
        <MainContent
          schoolConfig={data}
          isPreview={isPreview}
        />
      </ContentWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f6f6f6;

  @media (min-width: 768px) {
    flex-direction: row;
    overflow: auto;
  }

  ${userAgent.isMobile &&
  css`
    flex-direction: column;
    padding: 16px;
    overflow: auto;
  `}

  @media (max-width: 767px) {
    flex-direction: column;
    padding: 16px;
    overflow: auto;
  }
`

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;

  ${userAgent.isMobile &&
  css`
    padding: 16px;
  `}

  @media (max-width: 767px) {
    padding: 16px;
  }
`

const ImageWrapper = styled.div`
  height: 100vh;
  width: 640px;
  overflow: hidden;
  background-size: cover;
  min-height: 580px;

  ${userAgent.isMobile &&
  css`
    display: none;
  `}

  @media (max-width: 767px) {
    display: none;
  }
`

const Mask = styled.div`
  background-color: rgba(0, 0, 0, 0.12);
  width: 100%;
  height: 100%;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const OuterImg = styled.img`
  width: 164px;
`

const StyledSpin = styled(Spin)`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`
